import { DEOnly } from '@iwIntlProvider/CountryWrappers';
import { Tooltip } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';

import styles from './PersonDetails.module.css';
import { getApplicationStatus } from '../../../../api/lendingAPI/ApplicationSelector';
import { useIsInSelfServeSplitTest } from '../../../../customHooks/useIsInSelfServeSplitTest';
import { DateInput } from '../../../../forms/dateInput/index';
import { FormField } from '../../../../forms/formField/FormField';
import { NameField } from '../../../../forms/nameField/index';
import { getCountry } from '../../../../iwIntlProvider/CountryProvider';

const selector = formValueSelector('person');
const mapStateToProps = (state: RootState) => ({
    ...state.personModal,
    hasCreditDecision: getApplicationStatus(state) !== 'open',
    isGuarantor: selector(state, 'isGuarantor')
});

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

const PersonDetails = (props: PropsFromRedux) => {
    const intl = useIntl();
    const isInSelfServeSplitTest = useIsInSelfServeSplitTest();

    const { hasCreditDecision, isEditingPerson, isGuarantor } = props;
    const personalDetailsEditingDisabled =
        hasCreditDecision && isEditingPerson && isGuarantor;
    return (
        <>
            <NameField
                name="full_name"
                label={
                    <FormattedMessage
                        id="_.person.full_name"
                        defaultMessage="Full name"
                    />
                }
                disabled={personalDetailsEditingDisabled}
            />
            <DateInput
                name="dateOfBirth"
                label={
                    <FormattedMessage
                        id="_.person.date_of_birth"
                        defaultMessage="Date of birth"
                    />
                }
                disabled={personalDetailsEditingDisabled}
            />
            <label>
                <FormattedMessage
                    id="_.person.role_title"
                    defaultMessage="This person is a..."
                />
            </label>
            <FormField
                className={styles.checkboxGroup}
                name="isDirector"
                type="checkbox"
                label={
                    <FormattedMessage
                        id="_.company_role.director"
                        defaultMessage="Director"
                    />
                }
            />
            <FormField
                className={styles.checkboxGroup}
                name="isShareholder"
                type="checkbox"
                label={
                    <FormattedMessage
                        id="_.company_role.shareholder"
                        defaultMessage="Shareholder"
                    />
                }
            />
            <DEOnly>
                <div>
                    <div>
                        <label>
                            <FormattedMessage
                                id="_.person.additional_guarantor"
                                defaultMessage="Additional Guarantor"
                            />
                        </label>
                        <FormField
                            name="isGuarantor"
                            type="checkbox"
                            data-testid="isGuarantorDE"
                            label={
                                <FormattedMessage
                                    id="_.person.add_additional_guarantor"
                                    defaultMessage="I also want to add this person as a guarantor"
                                />
                            }
                        />
                    </div>
                    {isGuarantor ? (
                        <div className="inline-block rounded-m bg-secondary-90 p-xl">
                            <FormattedMessage
                                id="_.person.additional_guarantor_consent_msg"
                                defaultMessage="In adding this person as a guarantor, I consent to the checking of their personal creditworthiness"
                            />
                            <FormField
                                className={styles.checkboxGroup}
                                name="additionalGuarantorConsent"
                                type="checkbox"
                                label={
                                    <FormattedMessage
                                        id="_.person.consent_confirmation"
                                        defaultMessage="I consent"
                                    />
                                }
                            />
                        </div>
                    ) : null}
                </div>
            </DEOnly>
            {getCountry() === 'UK' ? (
                <div>
                    <div
                        className={styles.guarantorCheckbox}
                        data-tip
                        data-for="contactTooltip"
                    >
                        <FormField
                            className={styles.checkboxGroup}
                            name="isGuarantor"
                            data-testid="isGuarantor"
                            type="checkbox"
                            disabled={hasCreditDecision}
                            label={
                                <FormattedMessage
                                    id="_.company_role.guarantor"
                                    defaultMessage="Guarantor"
                                />
                            }
                        />
                        {hasCreditDecision && !isInSelfServeSplitTest ? (
                            <Tooltip
                                content={intl.formatMessage({
                                    id: '_.person.tooltip_message',
                                    defaultMessage:
                                        'Contact your account manager to change this'
                                })}
                                placement="right"
                                data-testid="contactTooltip"
                            />
                        ) : null}
                    </div>

                    {isGuarantor ? (
                        <div
                            className={styles.privacyPolicy}
                            data-testid="privacyPolicy"
                        >
                            <FormattedMessage
                                id="_.person.privacy_policy"
                                defaultMessage="I've shared the iwoca {privacyPolicyLink} with this person and they understand that their personal information will be used to run quotation searches with credit reference agencies, as well as fraud prevention agencies."
                                values={{
                                    privacyPolicyLink: (
                                        <a
                                            href="https://www.iwoca.co.uk/privacy-policy/"
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            <FormattedMessage
                                                id="_.person.privacy_policy_link"
                                                defaultMessage="privacy policy"
                                            />
                                        </a>
                                    )
                                }}
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(PersonDetails);
